/**
 * Governance Design System — Shared visual language for all Governance section pages
 *
 * Inspired by Credo.ai's enterprise governance aesthetic: clean, data-dense, premium.
 * Consistent tokens, components, and animations used across Policy Library, Trust Center,
 * Compliance Center, Gateway, Audit, and all Governance sub-pages.
 *
 * Usage:
 *   import { BRAND, S, ACCENT, GovStyles, StatCard, StatusBadge, ... } from '../components/common/GovernanceDesignSystem';
 */
import React from 'react';
import {
  Shield, FileCheck, Activity, Zap, Clock, Archive, Lock,
  AlertCircle, TrendingUp, Layers, Eye, ChevronRight, Search,
  FileText, X, RefreshCw, Plus, ArrowUpRight, BookOpen,
  BarChart3, ShieldCheck, CheckCircle2, Filter, ExternalLink,
} from 'lucide-react';

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ══════════════════════════════════════════════════════════════════════════════ */

/* Fix G46 + G53: BRAND used to be terracotta red — that's now reserved
 * for kill-switch + danger only. The platform's "ambient brand" identity
 * is the v2 deep-teal action color so every governance / canvas / chrome
 * consumer of BRAND inherits the rebirth palette automatically. */
export const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(13, 148, 136, 0.10)',
  mid: '#0e9c8f', soft: '#c3e9e3', muted: '#a5d8d0', tint: '#f0faf8',
  gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0a766b 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(13,148,136,0.05) 0%, rgba(10,118,107,0.02) 100%)',
};

/** Stone palette — warm neutrals with fine gradation */
export const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

/** Semantic accent palette — each with bg, text, border, icon */
export const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
  indigo:  { bg: '#eef2ff', text: '#3730a3', border: '#c7d2fe', icon: '#6366f1' },
  teal:    { bg: '#f0fdfa', text: '#134e4a', border: '#99f6e4', icon: '#14b8a6' },
  orange:  { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa', icon: '#f97316' },
};

/** Common status → visual mapping (for policy skills, agent statuses, etc.) */
export const STATUS_MAP = {
  draft:    { bg: S[100],      text: S[600],        border: S[200],        label: 'Draft',    icon: FileCheck, accent: ACCENT.slate },
  testing:  { bg: '#fef3c7',   text: '#92400e',     border: '#fde68a',     label: 'Testing',  icon: Activity,  accent: ACCENT.amber },
  active:   { bg: '#dcfce7',   text: '#166534',     border: '#a7f3d0',     label: 'Active',   icon: Zap,       accent: ACCENT.emerald },
  paused:   { bg: BRAND.light, text: BRAND.dark,    border: BRAND.soft,    label: 'Paused',   icon: Clock,     accent: ACCENT.rose },
  archived: { bg: S[100],      text: S[400],        border: S[200],        label: 'Archived', icon: Archive,   accent: ACCENT.slate },
  verified: { bg: '#dcfce7',   text: '#166534',     border: '#a7f3d0',     label: 'Verified', icon: CheckCircle2, accent: ACCENT.emerald },
  pending:  { bg: '#fef3c7',   text: '#92400e',     border: '#fde68a',     label: 'Pending',  icon: Clock,     accent: ACCENT.amber },
  suspended:{ bg: BRAND.light, text: BRAND.dark,    border: BRAND.soft,    label: 'Suspended',icon: AlertCircle,accent: ACCENT.rose },
  revoked:  { bg: '#fff1f2',   text: '#9f1239',     border: '#fecdd3',     label: 'Revoked',  icon: X,         accent: ACCENT.rose },
  expired:  { bg: S[100],      text: S[400],        border: S[200],        label: 'Expired',  icon: Clock,     accent: ACCENT.slate },
  healthy:  { bg: '#dcfce7',   text: '#166534',     border: '#a7f3d0',     label: 'Healthy',  icon: Zap,       accent: ACCENT.emerald },
  warning:  { bg: '#fef3c7',   text: '#92400e',     border: '#fde68a',     label: 'Warning',  icon: AlertCircle, accent: ACCENT.amber },
  critical: { bg: '#fff1f2',   text: '#9f1239',     border: '#fecdd3',     label: 'Critical', icon: AlertCircle, accent: ACCENT.rose },
  blocked:  { bg: '#fff1f2',   text: '#9f1239',     border: '#fecdd3',     label: 'Blocked',  icon: Shield,    accent: ACCENT.rose },
  allowed:  { bg: '#dcfce7',   text: '#166534',     border: '#a7f3d0',     label: 'Allowed',  icon: CheckCircle2, accent: ACCENT.emerald },
  completed:{ bg: '#dcfce7',   text: '#166534',     border: '#a7f3d0',     label: 'Completed',icon: CheckCircle2, accent: ACCENT.emerald },
  failed:   { bg: '#fff1f2',   text: '#9f1239',     border: '#fecdd3',     label: 'Failed',   icon: X,         accent: ACCENT.rose },
  running:  { bg: '#f0f9ff',   text: '#0c4a6e',     border: '#bae6fd',     label: 'Running',  icon: Activity,  accent: ACCENT.sky },
  rotating: { bg: '#f5f3ff',   text: '#5b21b6',     border: '#ddd6fe',     label: 'Rotating', icon: RefreshCw, accent: ACCENT.violet },
};

/* ══════════════════════════════════════════════════════════════════════════════
   STYLE PRIMITIVES
   ══════════════════════════════════════════════════════════════════════════════ */

export const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};

export const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
};

/* ══════════════════════════════════════════════════════════════════════════════
   GLOBAL CSS ANIMATIONS — inject once per page via <GovStyles />
   ══════════════════════════════════════════════════════════════════════════════ */

export function GovStyles() {
  return (
    <style>{`
      @keyframes govSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes govPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      @keyframes govSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes govShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      @keyframes govFadeIn { from { opacity: 0; } to { opacity: 1; } }
      .gov-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; transform: translateY(-1px); }
      .gov-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
      .gov-btn-ghost:hover { background: ${S[100]} !important; }
      .gov-btn-primary:hover { filter: brightness(1.08); box-shadow: 0 2px 8px rgba(226,74,58,0.25); }
      .gov-stat-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
      .gov-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
      .gov-search:focus { border-color: ${BRAND.primary}60 !important; box-shadow: 0 0 0 3px ${BRAND.glow} !important; }
      .gov-row:hover { background: ${S[25]} !important; }
      .gov-action-btn:hover { background: ${S[150]} !important; color: ${S[800]} !important; }
      .gov-link:hover { color: ${BRAND.primary} !important; }
    `}</style>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

/** Page header with icon, title, subtitle, role badge, and action buttons */
export function PageHeader({ icon: Icon, title, subtitle, actions, roleBadge }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: BRAND.gradientSubtle,
            border: `1px solid ${S[200]}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {Icon && <Icon size={17} color={BRAND.primary} />}
          </div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: S[900], letterSpacing: '-0.02em' }}>
            {title}
          </h1>
          {roleBadge && (
            <span style={{
              fontSize: 10, fontWeight: 600, color: BRAND.primary, background: BRAND.light,
              padding: '2px 8px', borderRadius: 6, border: `1px solid ${BRAND.soft}`,
            }}>{roleBadge}</span>
          )}
        </div>
        {subtitle && (
          <p style={{ margin: '2px 0 0 42px', fontSize: 12, color: S[500], lineHeight: 1.5 }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>}
    </div>
  );
}

/** Tab bar (horizontal) with optional count badges and link tabs */
export function TabBar({ tabs, activeTab, onTabChange }) {
  return (
    <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${S[200]}`, overflowX: 'auto' }}>
      {tabs.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            className={isActive ? '' : 'gov-tab'}
            onClick={() => tab.onClick ? tab.onClick() : onTabChange(tab.key)}
            style={{
              ...btnBase, padding: '11px 16px', background: 'transparent', borderRadius: 0,
              color: isActive ? BRAND.primary : S[500],
              fontSize: 12, fontWeight: isActive ? 700 : 500,
              borderBottom: isActive ? `2px solid ${BRAND.primary}` : '2px solid transparent',
              marginBottom: -1, flexShrink: 0, whiteSpace: 'nowrap',
            }}
          >
            {Icon && <Icon size={14} />}
            {tab.label}
            {tab.isLink && <ArrowUpRight size={11} style={{ opacity: 0.5 }} />}
            {tab.count != null && (
              <span style={{
                fontSize: 10, fontWeight: 600,
                background: isActive ? BRAND.light : S[100],
                color: isActive ? BRAND.primary : S[400],
                padding: '1px 7px', borderRadius: 8, marginLeft: 2,
              }}>{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Stat card for hero metric rows */
export function StatCard({ label, value, subtitle, icon: Icon, accent, loading: isLoading, onClick }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="gov-stat-card" onClick={onClick} style={{
      flex: 1, minWidth: 180, background: '#fff', border: `1px solid ${S[200]}`,
      borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s ease',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 8,
          }}>{label}</div>
          {isLoading ? (
            <div style={{ width: 48, height: 28, borderRadius: 6, background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`, backgroundSize: '200% 100%', animation: 'govShimmer 1.5s infinite' }} />
          ) : (
            <div style={{ fontSize: 30, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{value}</div>
          )}
          {subtitle && <div style={{ fontSize: 11, color: S[500], marginTop: 6, lineHeight: 1.3 }}>{subtitle}</div>}
        </div>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: a.bg, border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {Icon && <Icon size={17} color={a.icon} strokeWidth={2} />}
        </div>
      </div>
    </div>
  );
}

/** Status badge with icon */
export function StatusBadge({ status, size = 'default', label: overrideLabel }) {
  const s = STATUS_MAP[status?.toLowerCase()] || STATUS_MAP.draft;
  const Icon = s.icon;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: s.bg, color: s.text,
      border: `1px solid ${s.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      <Icon size={isSmall ? 10 : 11} />
      {overrideLabel || s.label}
    </span>
  );
}

/** Metric pill — compact inline stat */
export function MetricPill({ value, label, icon: Icon, trend, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, color: color || S[500], background: S[50], padding: '3px 10px',
      borderRadius: 6, border: `1px solid ${S[200]}`,
    }}>
      {Icon && <Icon size={11} color={S[400]} />}
      <strong style={{ color: color || S[700], fontWeight: 600 }}>{value}</strong>
      {label && <span>{label}</span>}
      {trend && <TrendingUp size={10} color={ACCENT.emerald.icon} />}
    </span>
  );
}

/** Search bar with clear button */
export function SearchInput({ value, onChange, placeholder = 'Search...', maxWidth = 360 }) {
  return (
    <div style={{ position: 'relative', flex: 1, maxWidth }}>
      <Search size={14} style={{ position: 'absolute', left: 11, top: 11, color: S[400], pointerEvents: 'none' }} />
      <input
        className="gov-search"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ ...inputBase, paddingLeft: 32 }}
      />
      {value && (
        <button onClick={() => onChange('')} style={{
          ...btnBase, position: 'absolute', right: 8, top: 8, padding: 3,
          background: S[100], color: S[500], borderRadius: 4,
        }}>
          <X size={12} />
        </button>
      )}
    </div>
  );
}

/** Error banner with dismiss */
export function ErrorBanner({ error, onDismiss }) {
  if (!error) return null;
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 10, marginBottom: 20,
      background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`,
      color: ACCENT.rose.text, fontSize: 13,
      display: 'flex', alignItems: 'center', gap: 8,
      animation: 'govSlideUp 0.3s ease',
    }}>
      <AlertCircle size={16} /> {error}
      {onDismiss && (
        <button onClick={onDismiss} style={{ ...btnBase, marginLeft: 'auto', padding: 4, background: 'transparent', color: ACCENT.rose.text }}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}

/** Empty state with icon, title, subtitle, and optional actions */
export function EmptyState({ icon: Icon, title, subtitle, actions }) {
  return (
    <div style={{
      textAlign: 'center', padding: '64px 32px', background: '#fff',
      border: `2px dashed ${S[200]}`, borderRadius: 16,
      animation: 'govFadeIn 0.4s ease',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
        background: BRAND.gradientSubtle, border: `1px solid ${S[200]}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {Icon ? <Icon size={28} color={S[400]} strokeWidth={1.5} /> : <Shield size={28} color={S[400]} strokeWidth={1.5} />}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: S[900], marginBottom: 8 }}>
        {title || 'Nothing here yet'}
      </div>
      {subtitle && (
        <div style={{ fontSize: 13, color: S[500], maxWidth: 440, margin: '0 auto 24px', lineHeight: 1.6 }}>
          {subtitle}
        </div>
      )}
      {actions && <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>{actions}</div>}
    </div>
  );
}

/** Loading skeleton — shimmer placeholder grid */
export function LoadingSkeleton({ count = 3, columns = 'repeat(auto-fill, minmax(340px, 1fr))' }) {
  const shimmer = {
    background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`,
    backgroundSize: '200% 100%', animation: 'govShimmer 1.5s infinite', borderRadius: 8,
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: columns, gap: 14 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
          padding: 20, animation: 'govFadeIn 0.3s ease',
          animationDelay: `${i * 0.1}s`, animationFillMode: 'backwards',
        }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, ...shimmer }} />
            <div style={{ flex: 1 }}>
              <div style={{ width: '60%', height: 16, marginBottom: 8, ...shimmer }} />
              <div style={{ width: '40%', height: 12, ...shimmer }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 60, height: 22, ...shimmer }} />
            <div style={{ width: 60, height: 22, ...shimmer }} />
            <div style={{ width: 80, height: 22, ...shimmer }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Loading row skeleton — for table/list views */
export function LoadingRows({ count = 5 }) {
  const shimmer = {
    background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`,
    backgroundSize: '200% 100%', animation: 'govShimmer 1.5s infinite', borderRadius: 6,
  };
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          padding: '14px 20px', borderBottom: `1px solid ${S[100]}`,
          display: 'flex', alignItems: 'center', gap: 14,
          animation: 'govFadeIn 0.3s ease', animationDelay: `${i * 0.05}s`, animationFillMode: 'backwards',
        }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, ...shimmer }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ width: `${50 + Math.random() * 30}%`, height: 14, ...shimmer }} />
            <div style={{ width: `${30 + Math.random() * 20}%`, height: 10, ...shimmer }} />
          </div>
          <div style={{ width: 70, height: 22, ...shimmer }} />
        </div>
      ))}
    </>
  );
}

/** Refresh button (ghost style) */
export function RefreshButton({ onClick, loading: isLoading }) {
  return (
    <button className="gov-btn-ghost" onClick={onClick} style={{
      ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
      background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
    }}>
      <RefreshCw size={13} style={isLoading ? { animation: 'govSpin 1s linear infinite' } : undefined} />
      Refresh
    </button>
  );
}

/** Primary action button (gradient brand) */
export function PrimaryButton({ children, onClick, icon: Icon, disabled }) {
  return (
    <button className={disabled ? '' : 'gov-btn-primary'} onClick={disabled ? undefined : onClick} style={{
      ...btnBase, padding: '8px 18px', fontSize: 12, fontWeight: 700,
      background: disabled ? S[200] : BRAND.gradient, color: disabled ? S[500] : '#fff',
      boxShadow: disabled ? 'none' : '0 1px 4px rgba(226,74,58,0.2)',
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}>
      {Icon && <Icon size={14} />}
      {children}
    </button>
  );
}

/** Ghost button (outlined) */
export function GhostButton({ children, onClick, icon: Icon }) {
  return (
    <button className="gov-btn-ghost" onClick={onClick} style={{
      ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
      background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
    }}>
      {Icon && <Icon size={13} />}
      {children}
    </button>
  );
}

/** Table wrapper with header row and optional loading state */
export function DataTable({ columns, children, loading: isLoading }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12, overflow: 'hidden',
      animation: 'govFadeIn 0.3s ease',
    }}>
      <div style={{
        display: 'grid', gridTemplateColumns: columns,
        gap: 0, padding: '10px 20px', background: S[50], borderBottom: `1px solid ${S[200]}`,
      }}>
        {/* Render header cells via children[0] or dedicated header prop */}
      </div>
      {isLoading ? <LoadingRows /> : children}
    </div>
  );
}

/** Section divider with label */
export function SectionLabel({ children, icon: Icon, actions }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 14, marginTop: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {Icon && <Icon size={14} color={S[500]} />}
        <span style={{ fontSize: 12, fontWeight: 700, color: S[700], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {children}
        </span>
      </div>
      {actions}
    </div>
  );
}

/** Card wrapper with hover effect */
export function Card({ children, style, onClick, className = '' }) {
  return (
    <div className={`gov-card ${className}`} onClick={onClick} style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      cursor: onClick ? 'pointer' : 'default', overflow: 'hidden',
      ...style,
    }}>
      {children}
    </div>
  );
}

/** Full-page layout wrapper (sticky header + scrollable content) */
export function GovPageLayout({ header, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', background: S[50] }}>
      <GovStyles />
      {/* Header strip — vertical padding so the PageHeader title doesn't
          ram into the breadcrumb bar above; horizontal padding mirrors
          the body's gutter so icon-square + title align with content
          below. Matches the standard 24px / 32px tokens used by
          PageContainer across the rest of the platform. The bottom
          border on this strip was REMOVED on 2026-05-04 because
          PageHeader supplies its own bottom divider; stacking both
          produced the double-line visual artifact reported on
          Governance Hub. */}
      <div style={{
        background: '#fff', flexShrink: 0,
        padding: '24px 32px 0',
      }}>
        {header}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px 40px' }}>
        {children}
      </div>
    </div>
  );
}

/** Badge — small colored tag */
export function Badge({ text, color = S[500], bg = S[100], border }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color, background: bg,
      padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
      border: border ? `1px solid ${border}` : undefined,
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      {text}
    </span>
  );
}

/** Filter select dropdown styled consistently */
export function FilterSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{ ...inputBase, width: 'auto', minWidth: 130, cursor: 'pointer', color: value === 'all' ? S[500] : S[900] }}
    >
      {placeholder && <option value="all">{placeholder}</option>}
      {options.map(opt => (
        <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
          {typeof opt === 'string' ? opt : opt.label}
        </option>
      ))}
    </select>
  );
}

/** View mode toggle (grid/list) */
export function ViewToggle({ viewMode, onViewChange }) {
  return (
    <div style={{ display: 'flex', border: `1px solid ${S[200]}`, borderRadius: 8, overflow: 'hidden' }}>
      {[
        { key: 'grid', icon: Layers },
        { key: 'list', icon: Filter },
      ].map(v => (
        <button key={v.key} onClick={() => onViewChange(v.key)} style={{
          ...btnBase, padding: '7px 10px', borderRadius: 0,
          background: viewMode === v.key ? S[100] : '#fff',
          color: viewMode === v.key ? S[800] : S[400],
          borderRight: v.key === 'grid' ? `1px solid ${S[200]}` : 'none',
        }}>
          <v.icon size={14} />
        </button>
      ))}
    </div>
  );
}

/** Results counter label */
export function ResultsCount({ filtered, total }) {
  return (
    <span style={{ fontSize: 11, color: S[400], whiteSpace: 'nowrap' }}>
      {filtered} of {total}
    </span>
  );
}

/** No results state (when filters produce empty) */
export function NoResults({ onClear }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: S[500] }}>
      <Search size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: S[700] }}>No matching results</div>
      <div style={{ fontSize: 12, color: S[500] }}>Try adjusting your search or filter criteria.</div>
      {onClear && (
        <button onClick={onClear} style={{
          ...btnBase, marginTop: 12, padding: '6px 14px', fontSize: 12,
          background: S[100], color: S[600], border: `1px solid ${S[200]}`,
        }}>
          Clear Filters
        </button>
      )}
    </div>
  );
}

/** Table column header cell */
export function ColHeader({ children, align = 'left' }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase',
      letterSpacing: '0.06em', textAlign: align,
    }}>{children}</div>
  );
}
